import { Injectable } from '@angular/core';
import { parse } from 'exifr';

type ImageSource = string | File | Blob;
type ImageFormat = 'image/jpeg' | 'image/png' | 'image/webp';
type ImageProcessingResult<T extends ImageSource> = T;

@Injectable({
  providedIn: 'root',
})
export class ImageUtilsService {
  private readonly supportedFormats: ImageFormat[] = ['image/jpeg', 'image/png', 'image/webp'];
  private readonly defaultQuality = 0.9;
  private readonly defaultFormat: ImageFormat = 'image/png';
  constructor() { }

  /**
 * Converte un elemento HTMLImageElement in una stringa base64, con possibilità di ridimensionamento.
 * Gestisce l'orientamento EXIF e ottimizza la gestione delle risorse di memoria.
 * 
 * @param imgElement - L'elemento HTML Image da convertire
 * @param maxSize - Dimensione massima in pixel (opzionale, se specificato l'immagine verrà ridimensionata)
 * @param format - Il formato di output dell'immagine (default: 'image/png')
 * @param quality - La qualità dell'immagine compressa tra 0 e 1 (default: 0.9)
 * @returns Promise con la stringa base64 dell'immagine
 * 
 * @example
 * // Converti un elemento immagine in base64 mantenendo le dimensioni originali
 * const base64String = await service.imageToBase64(imgElement);
 * 
 * @example
 * // Converti e ridimensiona a max 800px come JPEG con qualità 0.8
 * const base64Small = await service.imageToBase64(imgElement, 800, 'image/jpeg', 0.8);
 * 
 * @throws {Error} Se i parametri di input non sono validi o se il processo fallisce
 */
  async imageToBase64(
    imgElement: HTMLImageElement,
    maxSize?: number,
    format: ImageFormat = this.defaultFormat,
    quality: number = this.defaultQuality
  ): Promise<string> {
    // Validazione degli input
    this.validateInputs(maxSize, format, quality);

    let canvas: HTMLCanvasElement | null = null;

    try {
      // Ottieni l'orientamento EXIF
      const orientation = await this.getExifOrientation(imgElement);

      // Prepara dimensioni
      const { naturalWidth, naturalHeight } = imgElement;

      // Se c'è un maxSize, ridimensiona mantenendo le proporzioni
      let width = naturalWidth;
      let height = naturalHeight;

      if (maxSize && (width > maxSize || height > maxSize)) {
        [width, height] = this.calculateProportionalSize(width, height, maxSize);
      }

      // Crea canvas con l'orientamento corretto
      canvas = this.createOrientedCanvas(width, height, orientation);
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context not available');

      // Applica la trasformazione di orientamento
      this.applyOrientationTransform(ctx, canvas.width, canvas.height, orientation);

      // Disegna l'immagine sul canvas
      ctx.drawImage(imgElement, 0, 0, naturalWidth, naturalHeight, 0, 0, width, height);

      // Converti il canvas in base64
      return canvas.toDataURL(format, quality);
    } finally {
      // Pulisci le risorse
      this.disposeCanvas(canvas);
      // Non facciamo dispose dell'immagine perché è fornita dall'esterno
      // e potrebbe essere ancora in uso dal chiamante
    }
  }

  /**
 * Ridimensiona un'immagine mantenendo le proporzioni originali.
 * Gestisce automaticamente l'orientamento EXIF dell'immagine.
 * 
 * @param image - L'immagine da ridimensionare (stringa base64, File o Blob)
 * @param maxSize - La dimensione massima in pixel (sia per larghezza che altezza)
 * @param format - Il formato di output dell'immagine (default: 'image/png')
 * @param quality - La qualità dell'immagine compressa tra 0 e 1 (default: 0.85)
 * @returns Promise con l'immagine ridimensionata nello stesso tipo dell'input
 * 
 * @example
 * // Ridimensiona un'immagine base64 a max 1024px come png
 * const resized = await service.resizeImage(base64String, 1024);
 * 
 * @example
 * // Ridimensiona un File a max 800px come JPEG con qualità 0.7
 * const resizedFile = await service.resizeImage(file, 800, 'image/jpeg', 0.7);
 * 
 * @throws {Error} Se i parametri di input non sono validi o se il processo fallisce
 */
  async resizeImage<T extends ImageSource>(
    image: T,
    maxSize: number,
    format: ImageFormat = this.defaultFormat,
    quality: number = this.defaultQuality
  ): Promise<ImageProcessingResult<T>> {
    this.validateInputs(maxSize, format, quality);

    return this.processImage(
      image,
      format,
      quality,
      async (img, orientation) => {
        const { naturalWidth, naturalHeight } = img;
        const swapDimensions = orientation >= 5 && orientation <= 8;
        const originalWidth = swapDimensions ? naturalHeight : naturalWidth;
        const originalHeight = swapDimensions ? naturalWidth : naturalHeight;

        const [newWidth, newHeight] = this.calculateProportionalSize(
          originalWidth,
          originalHeight,
          maxSize
        );

        const canvas = this.createOrientedCanvas(newWidth, newHeight, orientation);
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas context not available');

        this.applyOrientationTransform(ctx, newWidth, newHeight, orientation);
        ctx.drawImage(img, 0, 0, originalWidth, originalHeight, 0, 0, newWidth, newHeight);

        return canvas;
      }
    ) as Promise<T>;
  }

  /**
 * Corregge l'orientamento di un'immagine in base ai suoi metadati EXIF.
 * Utile per visualizzare correttamente le immagini scattate con smartphone e fotocamere digitali.
 * Non modifica le dimensioni dell'immagine, solo il suo orientamento.
 * 
 * @param image - L'immagine da correggere (stringa base64, File o Blob)
 * @param format - Il formato di output dell'immagine (default: 'image/png')
 * @param quality - La qualità dell'immagine compressa tra 0 e 1 (default: 0.85)
 * @returns Promise con l'immagine ruotata correttamente nello stesso tipo dell'input
 * 
 * @example
 * // Correggi l'orientamento di un'immagine mantenendo il formato png
 * const corrected = await service.autoRotateImage(imageBlob);
 * 
 * @example
 * // Correggi l'orientamento convertendo in PNG a massima qualità
 * const correctedPng = await service.autoRotateImage(file, 'image/png', 1.0);
 * 
 * @throws {Error} Se i parametri di input non sono validi o se il processo fallisce
 */
  async autoRotateImage<T extends ImageSource>(
    image: T,
    format: ImageFormat = this.defaultFormat,
    quality: number = this.defaultQuality
  ): Promise<ImageProcessingResult<T>> {
    this.validateInputs(undefined, format, quality);

    return this.processImage(
      image,
      format,
      quality,
      async (img, orientation) => {
        if (orientation === 1) {
          const canvas = this.createCanvas(img.naturalWidth, img.naturalHeight);
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('Canvas context not available');
          ctx.drawImage(img, 0, 0);
          return canvas;
        }

        const { naturalWidth, naturalHeight } = img;
        const [width, height] = this.getOrientedDimensions(naturalWidth, naturalHeight, orientation);

        const canvas = this.createCanvas(width, height);
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas context not available');

        this.applyOrientationTransform(ctx, width, height, orientation);
        ctx.drawImage(img, 0, 0);

        return canvas;
      }
    ) as Promise<T>;
  }


  /**
 * Carica un'immagine e restituisce le sue dimensioni effettive in pixel.
 * Gestisce in modo efficiente le risorse per evitare memory leak.
 * 
 * @param source - L'immagine di cui ottenere le dimensioni (stringa base64, File o Blob)
 * @returns Promise con un oggetto contenente larghezza e altezza in pixel
 * 
 * @example
 * // Ottieni le dimensioni di un'immagine
 * const { width, height } = await service.getImageDimensions(imageFile);
 * console.log(`L'immagine è ${width}x${height} pixel`);
 * 
 * @example
 * // Verifica se un'immagine supera certe dimensioni prima di elaborarla
 * const dims = await service.getImageDimensions(base64Image);
 * if (dims.width > 4000 || dims.height > 4000) {
 *   console.warn("L'immagine è molto grande e potrebbe richiedere tempo per l'elaborazione");
 * }
 * 
 * @throws {Error} Se l'immagine non può essere caricata
 */
  async getImageDimensions(source: ImageSource): Promise<{ width: number; height: number }> {
    let img: HTMLImageElement | null = null;

    try {
      img = await this.loadImage(source);
      return {
        width: img.naturalWidth,
        height: img.naturalHeight
      };
    } finally {
      this.disposeImage(img);
    }
  }

  /**
   * Metodo principale per processare un'immagine
   */
  private async processImage<T extends ImageSource>(
    image: T,
    format: ImageFormat,
    quality: number,
    processor: (img: HTMLImageElement, orientation: number) => Promise<HTMLCanvasElement>
  ): Promise<T> {
    let img: HTMLImageElement | null = null;
    let canvas: HTMLCanvasElement | null = null;

    try {
      img = await this.loadImage(image);
      const orientation = await this.getExifOrientation(img);
      canvas = await processor(img, orientation);
      return await this.exportCanvasData(canvas, format, quality, image);
    } finally {
      this.safeDisposeResources(img, canvas);
    }
  }

  private validateInputs(maxSize?: number, format?: ImageFormat, quality?: number): void {
    if (maxSize !== undefined && maxSize <= 0) {
      throw new Error('Invalid max size');
    }
    if (format && !this.supportedFormats.includes(format)) {
      throw new Error(`Unsupported format: ${format}`);
    }
    if (quality !== undefined && (quality < 0 || quality > 1)) {
      throw new Error('Quality must be between 0 and 1');
    }
  }

  private async loadImage(source: ImageSource): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      let objectUrl: string | null = null;

      const cleanup = () => {
        img.onload = null;
        img.onerror = null;
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
        }
      };

      img.onload = () => {
        cleanup();
        resolve(img);
      };

      img.onerror = (err) => {
        cleanup();
        reject(new Error('Failed to load image', { cause: err }));
      };

      if (typeof source === 'string') {
        img.src = source;
      } else {
        objectUrl = URL.createObjectURL(source);
        img.src = objectUrl;
      }
    });
  }

  private async getExifOrientation(img: HTMLImageElement): Promise<number> {
    try {
      const exif = await parse(img);
      return exif?.Orientation ?? 1;
    } catch {
      return 1;
    }
  }

  private calculateProportionalSize(
    width: number,
    height: number,
    maxSize: number
  ): [number, number] {
    const ratio = Math.min(maxSize / width, maxSize / height);
    return [
      Math.round(width * ratio),
      Math.round(height * ratio)
    ];
  }

  private createOrientedCanvas(
    width: number,
    height: number,
    orientation: number
  ): HTMLCanvasElement {
    return this.createCanvas(
      orientation >= 5 && orientation <= 8 ? height : width,
      orientation >= 5 && orientation <= 8 ? width : height
    );
  }

  private createCanvas(width: number, height: number): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
  }

  private applyOrientationTransform(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    orientation: number
  ): void {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, width, height);

    switch (orientation) {
      case 2: ctx.transform(-1, 0, 0, 1, width, 0); break;
      case 3: ctx.transform(-1, 0, 0, -1, width, height); break;
      case 4: ctx.transform(1, 0, 0, -1, 0, height); break;
      case 5: ctx.transform(0, 1, 1, 0, 0, 0); break;
      case 6: ctx.transform(0, 1, -1, 0, height, 0); break;
      case 7: ctx.transform(0, -1, -1, 0, height, width); break;
      case 8: ctx.transform(0, -1, 1, 0, 0, width); break;
      default: ctx.setTransform(1, 0, 0, 1, 0, 0);
    }
  }

  private async exportCanvasData<T extends ImageSource>(
    canvas: HTMLCanvasElement,
    format: ImageFormat,
    quality: number,
    originalImage: T
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      if (originalImage instanceof File) {
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error('Conversion failed'));
            resolve(new File([blob], originalImage.name, {
              type: format,
              lastModified: originalImage.lastModified
            }) as T);
          },
          format,
          quality
        );
      } else if (originalImage instanceof Blob) {
        canvas.toBlob(
          (blob) => blob ? resolve(blob as T) : reject(new Error('Conversion failed')),
          format,
          quality
        );
      } else {
        resolve(canvas.toDataURL(format, quality) as T);
      }
    });
  }

  private getOrientedDimensions(
    width: number,
    height: number,
    orientation: number
  ): [number, number] {
    return orientation >= 5 && orientation <= 8 ? [height, width] : [width, height];
  }

  private safeDisposeResources(
    img: HTMLImageElement | null,
    canvas: HTMLCanvasElement | null
  ): void {
    try {
      this.disposeImage(img);
      this.disposeCanvas(canvas);
    } catch (error) {
      console.warn('Error during resource cleanup:', error);
    }
  }

  public disposeImage(img: HTMLImageElement | null): void {
    if (img) {
      img.onload = null;
      img.onerror = null;
      if (img.src.startsWith('blob:')) {
        URL.revokeObjectURL(img.src);
      }
      img.removeAttribute('src');
      if (img.parentNode) {
        img.parentNode.removeChild(img);
      }
    }
  }

  private disposeCanvas(canvas: HTMLCanvasElement | null): void {
    if (canvas) {
      canvas.width = 1;
      canvas.height = 1;
      const ctx = canvas.getContext('2d');
      ctx?.clearRect(0, 0, 1, 1);
      canvas.remove();
    }
  }

  /**
 * Converte una stringa base64 in un oggetto Blob.
 * Supporta stringhe base64 con o senza il prefisso "data:image/...;base64,".
 * 
 * @param base64 - La stringa base64 da convertire
 * @param mimeType - Il tipo MIME del Blob risultante (opzionale, verrà estratto dal prefisso se non specificato)
 * @returns Un oggetto Blob contenente i dati dell'immagine
 * 
 * @example
 * // Converti un'immagine base64 in Blob
 * const imageBlob = service.base64ToBlob('data:image/jpeg;base64,/9j/4AAQSkZJRg...');
 * 
 * @example
 * // Converti una stringa base64 pura specificando il tipo MIME
 * const blob = service.base64ToBlob('/9j/4AAQSkZJRg...', 'image/jpeg');
 * 
 * @throws {Error} Se la stringa base64 non è valida o se il processo fallisce
 */
  base64ToBlob(base64: string, mimeType?: string): Blob {
    try {
      // Verifica se la stringa include il prefisso data URL
      let dataContentParts: string[] = [];
      let binaryString: string;

      if (base64.startsWith('data:')) {
        dataContentParts = base64.split(',');
        if (dataContentParts.length !== 2) {
          throw new Error('Invalid base64 format');
        }

        // Estrai il tipo MIME dalla data URL se non è stato specificato
        if (!mimeType && dataContentParts[0]) {
          const matches = dataContentParts[0].match(/data:([^;]+);/);
          if (matches && matches.length > 1) {
            mimeType = matches[1];
          }
        }

        binaryString = atob(dataContentParts[1]);
      } else {
        // Se è una stringa base64 pura senza prefisso
        binaryString = atob(base64);

        // Usa il tipo MIME fornito o default
        if (!mimeType) {
          mimeType = this.defaultFormat;
        }
      }

      // Converti la stringa binaria in un array di byte
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Crea e restituisci il Blob
      return new Blob([bytes], { type: mimeType });
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to convert base64 to Blob: ${error.message}`, { cause: error });
      } else {
        throw new Error('Failed to convert base64 to Blob');
      }
    }
  }
}